import * as Sentry from "@sentry/browser";

// Drives HubSpot's v2 form embed for every `[data-hs-form-id]` container on the page.
// Extracted from what #97 built for /book-a-demo so #142 could put the contact form on
// the same path instead of a second one — the portal is the source of truth for both, and
// the failure, focus and a11y handling below is the part that is genuinely hard to redo.
//
// HubSpot hands you two synchronous <script> tags to paste into the page. That pair is
// not usable as-is: the external one blocks render, and `defer` is ignored on the inline
// call, so there is no declarative way to sequence `hbspt.forms.create` after the library
// loads. pricing.njk can inline Stripe's tag because a pricing table is a custom element
// that self-initializes; this needs an imperative call, so it belongs in the bundle.
//
// Keeping it here also gives #96 (cookie consent) a single place to gate the third-party
// request, instead of a <script> already committed to firing by the time JS runs.
const EMBED_SRC = "https://js.hsforms.net/forms/embed/v2.js";

// `load` and `error` between them cover outright refusal and network failure, but not a
// request that is black-holed rather than rejected — a common blocker behaviour. Neither
// event fires, so without a deadline the fallback would stay hidden forever on the two
// pages where an empty container is most of the page.
const LOAD_TIMEOUT_MS = 8000;

class HubSpotForm {
    constructor(target) {
        this.target = target;
        // By convention, not configuration: the fallback copy lives in the template next
        // to the container and is named after it. A container without one degrades to
        // "nothing appears", which is what the <noscript> and the Sentry warning are for.
        //
        // getElementById rather than querySelector with an interpolated `#id`. The id comes
        // from a template, so a value that is a valid HTML id but an invalid CSS selector —
        // a leading digit, a dot, a colon — makes querySelector throw SyntaxError. This runs
        // in the constructor, inside the map below, at import time: one bad id would take
        // out every form on the page rather than just its own. getElementById cannot throw.
        this.fallback = document.getElementById(`${target.id}-fallback`);
        this.timer = null;
    }

    revealFallback() {
        if (this.fallback) this.fallback.hidden = false;
    }

    // Reported explicitly because none of these paths throw, and Sentry's global handlers
    // only see *unhandled* errors. Without this, js.hsforms.net going down degrades the
    // two highest-intent pages on the site to a mailto link and nobody finds out.
    //
    // Scope is *rendering* only, which narrowed with #142. The deleted contact-form.js owned
    // its own POST and captured a HubSpot rejection, so a submit path that started failing
    // raised an event. Submission belongs to HubSpot now, and a failure after the form
    // renders is invisible from here — it surfaces in the portal, not in Sentry. Nothing
    // below closes that gap; do not read this as covering it.
    //
    // `captureMessage` at warning, never `captureException`. An ad blocker produces exactly
    // the same signal as a real outage — there is no way to tell them apart from here — and
    // blockers are far more common, so raising exceptions would bury genuine errors under an
    // expected, non-actionable condition. It also under-reports either way, since a blocker
    // aggressive enough to stop js.hsforms.net usually stops Sentry's ingest too.
    //
    // The container id is in the message so contact and demo failures group separately in
    // Sentry — they are different problems with different owners.
    fail(reason) {
        this.revealFallback();
        Sentry.captureMessage(`HubSpot form "${this.target.id}" did not render: ${reason}`, "warning");
    }

    settle() {
        if (this.timer === null) return;

        clearTimeout(this.timer);
        this.timer = null;
    }

    // HubSpot renders each radio group with ARIA that contradicts itself: role="checkbox"
    // on the <ul>, and role="radio" on every <li> wrapping the real <input type="radio">.
    // A screen reader is told the group is a checkbox and that each option is a radio
    // containing another radio, and the group's own label is a bare <label> with no `for`,
    // so it names nothing.
    //
    // A no-op on a form without radio groups, which is why it stays here rather than
    // moving to the one page that currently has them.
    static fixRadioGroups(form) {
        form.querySelectorAll(".hs-fieldtype-radio").forEach((field, index) => {
            const list = field.querySelector("ul.inputs-list");
            const label = field.querySelector(":scope > label");

            // Both halves are required. Promoting the list with nothing to name it from
            // leaves an unnamed radiogroup — a worse end state than the malformed markup
            // this repairs, and one that fails silently with no build or lint signal.
            if (!list || !label) return;

            // HubSpot does ship ids today, but this is markup we do not control and have
            // already found two defects in, so mint one rather than depend on it.
            if (!label.id) label.id = `hs-radio-group-${index}`;

            list.setAttribute("role", "radiogroup");
            list.setAttribute("aria-labelledby", label.id);

            // role="none", not removeAttribute: an <li> inside a <ul> regains its implicit
            // `listitem` role the moment the explicit one is gone, which leaves
            // radiogroup > listitem > radio. A radiogroup's owned children have to be
            // radios. Matching bare `li` rather than `li[role]` also covers any option
            // HubSpot ships without a role.
            list.querySelectorAll(":scope > li").forEach((item) => item.setAttribute("role", "none"));
        });
    }

    // "Roster Size" and "Number of Employees" are stored in the portal with a trailing
    // non-breaking space, which renders as a visible gap before the required asterisk.
    // Cleaning the property labels in HubSpot is the actual fix; this stops the page
    // showing it in the meantime, and becomes a no-op once that happens.
    static trimLabels(form) {
        form.querySelectorAll(".hs-form-field > label > span:first-child").forEach((span) => {
            // JS \s already covers U+00A0, so no separate NBSP literal is needed.
            span.textContent = span.textContent.replace(/\s+$/, "");
        });
    }

    // HubSpot marks a failed control with `class="hs-input invalid error"` and nothing else:
    // no `aria-invalid`, and no `aria-describedby` pointing at the message it just inserted.
    // Measured on /contact-us, not assumed. The per-field `<ul class="hs-error-msgs">` does
    // carry `role="alert"`, so the error is announced when it appears — but the control
    // itself never reports as invalid, and a screen-reader user who tabs back to the field
    // afterwards is told nothing. The hand-rolled form #142 replaced set both, so without
    // this the swap is an accessibility regression rather than a wash.
    //
    // Restoring them here also makes the `&[aria-invalid="true"]` branch in _hs-form.scss
    // live; it was written against this exact gap and had matched nothing until now.
    static syncErrorA11y(form, scope) {
        form.querySelectorAll(".hs-form-field").forEach((field, index) => {
            const control = field.querySelector(".hs-input");

            if (!control) return;

            // Scoped to the field, so the submit-level `.hs_error_rollup` — which sits
            // outside every .hs-form-field — cannot be mistaken for this field's message.
            const list = field.querySelector(".hs-error-msgs");
            const invalid = !!list && list.textContent.trim() !== "";

            if (!invalid) {
                // Unconditional, so this owns aria-describedby outright rather than only the
                // token it wrote. Safe today — HubSpot sets none on either form, checked by
                // instrumenting setAttribute before any page script ran — but a portal-side
                // field description wired through describedby would be stripped here on the
                // first class mutation after the field goes valid. If help text ever appears
                // in the portal, make this remove only the id minted below.
                control.removeAttribute("aria-invalid");
                control.removeAttribute("aria-describedby");
                return;
            }

            if (!list.id) list.id = `hs-error-${scope}-${index}`;

            control.setAttribute("aria-invalid", "true");
            control.setAttribute("aria-describedby", list.id);
        });
    }

    // Errors appear and clear as the visitor corrects the form, so this has to keep up
    // rather than run once. `attributeFilter: ["class"]` is what makes that safe: the
    // attributes syncErrorA11y writes — aria-invalid, aria-describedby and the minted id —
    // are all outside the filter, so the observer cannot retrigger itself.
    watchErrors(form) {
        const sync = () => HubSpotForm.syncErrorA11y(form, this.target.id);

        sync();
        new MutationObserver(sync).observe(form, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["class"],
        });
    }

    // HubSpot swaps the form out for .submitted-message and moves nothing, so a keyboard
    // or screen-reader user is left focused on a submit button that no longer exists.
    //
    // Observed rather than polled: onFormSubmitted fires on submit, before HubSpot has
    // swapped the message in, so it is never present on the first tick. An earlier version
    // retried across a fixed number of frames, which silently dropped focus management if
    // the swap took longer than the budget — the observer has no such ceiling and stops
    // itself once it fires.
    focusSuccessWhenReady() {
        const take = () => {
            const message = this.target.querySelector(".submitted-message");

            if (!message) return false;

            message.setAttribute("tabindex", "-1");
            message.focus();
            return true;
        };

        if (take()) return;

        const observer = new MutationObserver(() => {
            if (take()) observer.disconnect();
        });

        observer.observe(this.target, { childList: true, subtree: true });
    }

    create() {
        if (!window.hbspt?.forms) {
            // The script resolved but left no global — what a blocker returning 200 with
            // an empty body looks like, and the `error` event never fires for it.
            this.settle();
            this.fail("embed script loaded without defining hbspt");
            return;
        }

        const { hsPortalId: portalId, hsFormId: formId, hsRegion: region } = this.target.dataset;

        window.hbspt.forms.create({
            portalId,
            formId,
            region,
            // Explicit target is load-bearing. Left off, the library appends the form
            // wherever the executing script sits — which for a deferred bundle is the end
            // of <body>, below the footer, not the container on this page.
            //
            // CSS.escape because this is a *selector*, which HubSpot resolves with its own
            // querySelector. The constructor deliberately uses getElementById so that an id
            // that is valid HTML but invalid CSS cannot throw; interpolating that same id
            // raw here would hand the problem straight back, and since create() runs from a
            // forEach in the load handler the throw would still take out every form after
            // this one. Escaping is what makes the constructor's isolation hold end to end.
            target: `#${CSS.escape(this.target.id)}`,
            // Fires once the form is in the DOM. `form` is a jQuery-like collection from
            // HubSpot's bundled jQuery, so unwrap to the element before touching it.
            onFormReady: (form) => {
                this.settle();

                // The deadline may already have fired on a slow connection. The form is
                // here now, so retract the fallback rather than showing both.
                if (this.fallback) this.fallback.hidden = true;

                const elem = form instanceof Element ? form : form?.[0];

                if (!elem) return;

                HubSpotForm.fixRadioGroups(elem);
                HubSpotForm.trimLabels(elem);
                this.watchErrors(elem);
            },
            onFormSubmitted: () => this.focusSuccessWhenReady(),
        });
    }

    start() {
        this.timer = setTimeout(() => {
            this.timer = null;
            this.fail(`still absent after ${LOAD_TIMEOUT_MS}ms`);
        }, LOAD_TIMEOUT_MS);
    }
}

// Matched on `[data-hs-form-id][id]`, so both halves the create() call needs are asserted
// by the selector: a container with no form id is inert and would ask HubSpot for a form
// that cannot exist, and one with no id of its own cannot be named as create()'s target
// or resolve its own fallback.
const targets = [...document.querySelectorAll("[data-hs-form-id][id]")];

if (targets.length) {
    const forms = targets.map((target) => new HubSpotForm(target));

    // One <script> for the page however many containers it holds: hbspt is a single global
    // and a second tag would race the first to define it. Each form arms its own deadline
    // before the request goes out, so a black-holed script still surfaces per container.
    forms.forEach((form) => form.start());

    const script = document.createElement("script");

    script.src = EMBED_SRC;
    script.async = true;
    script.addEventListener("load", () => forms.forEach((form) => form.create()));
    script.addEventListener("error", () => {
        forms.forEach((form) => {
            form.settle();
            form.fail("embed script failed to load");
        });
    });

    document.head.appendChild(script);
}
